/**
 * Why bookings are cancelled (Trera, Oct 2026: "start storing top categories of why, with an Other, so we can pull
 * that up on Reports & Analytics"). The categories come from what staff actually typed on the 82 cancellations to
 * date. Staff pick one in the Cancel dialog (bookings.cancellation_category) and still write the reason.
 *
 * Older cancellations have no category; Reports sorts them from the typed reason (inferCancelCategory) and says so.
 * Pure.
 */
export const CANCEL_CATEGORIES = [
  { key: 'budget', label: 'Budget or finances' },
  { key: 'unpaid', label: "Didn't pay the balance (we cancelled)" },
  { key: 'event_off', label: 'Wedding cancelled or postponed' },
  { key: 'life_change', label: 'Life change (health, family, moving)' },
  { key: 'booking_error', label: 'Booking mistake or re-booking' },
  { key: 'other_vendor', label: 'Went with another vendor' },
  { key: 'unhappy', label: 'Unhappy with us' },
  { key: 'other', label: 'Other' }
]
const KEYS = new Set(CANCEL_CATEGORIES.map(c => c.key))
export const isCancelCategory = k => KEYS.has(String(k || ''))
export const cancelCategoryLabel = k => CANCEL_CATEGORIES.find(c => c.key === k)?.label || 'Other'

/** The category a typed reason most likely belongs to (older cancellations). Order matters: the first match wins. */
const RULES = [
  ['unpaid', /never paid|didn'?t pay|did not pay|unable to (make|pay) (their |her |his |the )?(final|remaining)|remaining balance|missed (payment|payments)|non.?payment|failed payment/i],
  ['booking_error', /technical|processing fee|re-?book|wrong (date|package|option)|by mistake|mistaken|selected the wrong|duplicate|booked twice|accident/i],
  ['other_vendor', /another (photographer|videographer|vendor|company)|went with|hired (someone|a different)|found someone|different photographer|their venue includes/i],
  ['unhappy', /unhappy|not happy|dissatisfied|complain|bad experience|poor (service|communication)|refund request/i],
  ['life_change', /pregnan|diagnos|cancer|illness|sick|passed away|death|deploy|military|moving|relocat|family emergency|health/i],
  ['event_off', /wedding (was |is |has been )?(cancel|called off|off|postpon)|called off|postpon|eloped|elope|no longer (getting married|having)|broke up|engagement (ended|off)|date change|changed (the )?date|happened early/i],
  ['budget', /financ|money|afford|budget|cost|expens|funds|job loss|lost (his|her|their) job/i]
]
export function inferCancelCategory(reason) {
  const text = String(reason || '')
  for (const [key, re] of RULES) if (re.test(text)) return key
  return 'other'
}

/** A cancellation's category: the one staff picked, else sorted from its reason. */
export function cancelCategoryOf(b) {
  if (isCancelCategory(b?.cancellation_category)) return { key: b.cancellation_category, inferred: false }
  return { key: inferCancelCategory(b?.cancellation_reason), inferred: true }
}

/**
 * Reports: cancellations in the period by category, most first, with how many were sorted from the reason, and the
 * typed reasons behind "Other" so they can be read.
 * @param cancelled  bookings cancelled in the period (cancellation_category, cancellation_reason)
 */
export function cancellationBreakdown(cancelled) {
  const list = cancelled || []
  const counts = new Map(CANCEL_CATEGORIES.map(c => [c.key, { key: c.key, label: c.label, count: 0, inferred: 0 }]))
  const other = []
  for (const b of list) {
    const { key, inferred } = cancelCategoryOf(b)
    const row = counts.get(key)
    row.count++
    if (inferred) row.inferred++
    if (key === 'other' && b?.cancellation_reason) other.push(String(b.cancellation_reason).replace(/\s+/g, ' ').trim().slice(0, 200))
  }
  const total = list.length
  const rows = [...counts.values()].filter(r => r.count > 0)
    .map(r => ({ ...r, percent: total ? Math.round((r.count / total) * 100) : 0 }))
    .sort((a, b) => b.count - a.count || CANCEL_CATEGORIES.findIndex(c => c.key === a.key) - CANCEL_CATEGORIES.findIndex(c => c.key === b.key))
  return { total, rows, inferred: rows.reduce((s, r) => s + r.inferred, 0), other }
}
